import { MessagingChannelType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';

import { Order } from '~hooks/useOrders/models';
import useOrdersDispatch from '~hooks/useOrders/useOrdersDispatch';
import TargetMessagingPanel from '~pages/Dispatch/components/TargetMessaging/TargetMessagingPanel';
import {
  MESSAGING_HEADER_AVATAR_SIZE_IN_PX,
  MessagingGlyphAvatar,
} from '~pages/Messages/components/MessagingAvatar';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

type OrderThreadPanelProps = {
  order: Order;
  readOnly?: boolean;
};

/**
 * Counts drivers from the order's jobs as the Jobs tab loads them, so a Dispatch filter that
 * hides some jobs also hides their drivers from the count.
 */
const OrderThreadPanel = observer(({ order, readOnly }: OrderThreadPanelProps) => {
  const { ordersDispatchStore, userStore } = useStores();
  const { getJobsByOrder } = useOrdersDispatch();
  const getJobsByOrderRef = useRef(getJobsByOrder);
  getJobsByOrderRef.current = getJobsByOrder;
  const companyId = getEffectiveUserCompanyId(userStore);
  const hasOrderJobs = ordersDispatchStore.orderJobs.has(order.id);

  const hasOrderJobsFetchFailed = Boolean(
    ordersDispatchStore.hasOrderJobsFetchFailed.get(order.id),
  );
  const hasRequestedOrderJobs = useRef(false);

  // Opened straight from Message all drivers, the Jobs tab never mounts to load the count; a
  // failed load retries once per visit, so a persistent failure cannot loop.
  useEffect(() => {
    const needsOrderJobs = !hasOrderJobs || hasOrderJobsFetchFailed;
    if (!needsOrderJobs || hasRequestedOrderJobs.current) return;

    hasRequestedOrderJobs.current = true;
    void getJobsByOrderRef.current({ companyId, orderId: order.id }).catch(() => {
      // Error is handled by the hook
    });
  }, [companyId, hasOrderJobs, hasOrderJobsFetchFailed, order.id]);

  const loadedOrderJobs = ordersDispatchStore.orderJobs.get(order.id);
  const isLoadingOrderJobs = Boolean(
    ordersDispatchStore.isLoadingOrderJobs.get(order.id),
  );
  const isDriverCountKnown =
    Boolean(loadedOrderJobs) && !isLoadingOrderJobs && !hasOrderJobsFetchFailed;
  const driverCount = new Set(
    (loadedOrderJobs ?? []).flatMap((job) => (job.driver ? [job.driver.id] : [])),
  ).size;

  // Order channel members are fixed at creation, so a thread started now would never reach later drivers.
  const hasNoDriversYet = isDriverCountKnown && driverCount === 0;

  // A failed count blocks the start instead of passing for an order with no drivers.
  const startBlockedMessage = () => {
    if (hasOrderJobsFetchFailed) return t('messaging.order_tab.drivers_unavailable');
    if (hasNoDriversYet) return t('messaging.order_tab.no_drivers_yet');

    return undefined;
  };

  const orderContext = [
    order.dropOffWayPoints?.[0]?.site?.fullAddress,
    order.material?.name,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <TargetMessagingPanel
      canCreateChannel={order.messagingEnabled}
      channelName={order.orderId}
      channelType={MessagingChannelType.ORDER}
      composerNote={
        isDriverCountKnown
          ? t('messaging.order_tab.drivers_on_order', { count: driverCount })
          : undefined
      }
      firstMessageHint={t('messaging.order_tab.first_message_hint')}
      headerAvatar={
        <MessagingGlyphAvatar
          channelType={MessagingChannelType.ORDER}
          size={MESSAGING_HEADER_AVATAR_SIZE_IN_PX}
        />
      }
      headerSubtitle={orderContext}
      headerTitle={order.orderId}
      isPreparingFirstMessage={!isDriverCountKnown}
      noMessagesMessage={t('messaging.order_tab.no_messages')}
      noPermissionMessage={t('messaging.order_tab.no_permission_to_start')}
      readOnly={readOnly}
      startBlockedMessage={startBlockedMessage()}
      targetId={order.id}
    />
  );
});

export default OrderThreadPanel;
